/**
 * CT-RM-30: the English (default) string catalog. Every key here is the source
 * of truth; other catalogs may omit keys and fall back to English. Keys under
 * public prefixes (common, switcher, give, landing, steps, volunteer, p2p) must
 * also exist in es.ts — catalog.test.ts enforces that.
 *
 * Placeholders: {name}. Plurals: "key.one" / "key.other", picked by {count}.
 */
export const en = {
  // ---- shared public chrome ----
  "common.cancel": "Cancel",
  "common.firstName": "First name",
  "common.lastName": "Last name",
  "common.email": "Email",
  "common.phone": "Phone",
  "common.optional": "Optional",
  "common.yourEmail": "Your email",
  "common.emailMeLink": "Email me a link",
  "common.checkEmail": "Check your email",
  "common.processing": "Processing…",
  "common.loading": "Loading",
  "common.thankYou": "Thank you!",
  "switcher.label": "Language",

  // ---- hosted giving page (/give/:slug) ----
  "give.notFound": "Giving page not found",
  "give.tagline": "Your gift makes our work possible. Thank you.",
  "give.demoBanner": "Demo mode: no payment will be collected and email delivery is not guaranteed.",
  "give.checkoutComplete": "Thank you. Your payment confirmation is being processed. Your receipt will be emailed once confirmed.",
  "give.receivedOnce": "Your gift of {amount} was received.",
  "give.receivedMonthly": "Your monthly gift of {amount} was received.",
  "give.receiptCreated": "Receipt {number} has been created. Email delivery is tracked separately.",
  "give.once": "Give once",
  "give.monthly": "Give monthly",
  "give.otherAmount": "Other amount",
  "give.designate": "Designate my gift to",
  "give.whereNeeded": "Where it's needed most",
  "give.emailForReceipt": "Email (for your receipt)",
  "give.dedicate": "Dedicate this gift (optional)",
  "give.noDedication": "No dedication",
  "give.inHonor": "In honor of",
  "give.inMemory": "In memory of",
  "give.honoreeName": "Name",
  "give.coverFees": "Add {amount} to cover processing fees so 100% of my gift goes to the mission",
  "give.demoCard": "Demo mode: no card required. Production uses Stripe-hosted card fields.",
  "give.submit": "Give {amount}",
  "give.submitMonthly": "Give {amount} monthly",
  "give.footer": "Secure giving powered by CauseTide · You'll receive a tax receipt by email",
  "give.unavailable": "Online giving is not yet available. Please contact this organization.",
  "give.minimum": "Minimum donation is {amount}",

  // ---- builder landing pages (/p/:slug/:page) ----
  "landing.notFound": "Page not found",
  "landing.renewLapsed": "Welcome back — your membership lapsed on {date}. Renew below.",
  "landing.renewCurrent": "Renew your membership — your current term runs through {date}.",
  "landing.testMode": "Test mode — no payment is processed and no email is sent.",
  "landing.choose": "Choose…",
  "landing.submitting": "Submitting…",
  "landing.oneTime": "One time",
  "landing.monthly": "Monthly",
  "landing.customAmount": "Custom amount",
  "landing.anonymous": "Give anonymously (don't list my name publicly)",
  "landing.perMonth": "/mo",
  "landing.simulated": "Payments are simulated in this environment.",
  "landing.receiptOnWay": "Receipt {number} is on its way to your inbox.",
  "landing.testModeDone": "Test mode — no payment was processed.",
  "embed.unavailable": "This form isn't available.",

  // ---- multi-step forms (StepForm) ----
  "steps.yourGift": "Your gift",
  "steps.yourDetails": "Your details",
  "steps.review": "Review",
  "steps.submit": "Submit",
  "steps.progress": "Form progress",
  "steps.completed": " (completed)",
  "steps.current": " (current)",
  "steps.stepOf": "Step {n} of {total}",
  "steps.fix.one": "Please fix this before continuing:",
  "steps.fix.other": "Please fix these {count} before continuing:",
  "steps.frequency": "Frequency",
  "steps.giftAmount": "Gift amount",
  "steps.otherAmount": "Other amount",
  "steps.required": " (required)",
  "steps.gift": "Gift",
  "steps.amountMonthly": "{amount} monthly",
  "steps.editGiftAmount": "gift amount",
  "steps.edit": "Edit",
  "steps.yes": "Yes",
  "steps.back": "Back",
  "steps.next": "Next",

  // ---- cart giving: multiple designations on a giving block (CT-RM-31) ----
  "landing.cart.title": "Your gifts",
  "landing.cart.fund": "Fund",
  "landing.cart.amount": "Amount",
  "landing.cart.frequency": "How often",
  "landing.cart.line": "Gift {n}",
  "landing.cart.addLine": "Add another fund",
  "landing.cart.removeLine": "Remove gift {n}",
  "landing.cart.remove": "Remove",
  "landing.cart.otherAmount": "Other amount",
  "landing.cart.subtotal": "Subtotal",
  "landing.cart.fees": "Processing fees",
  "landing.cart.total": "Total today",
  "landing.cart.monthlyNote": "{amount} of it repeats monthly",
  "landing.cart.coverFees": "Add {amount} to cover processing fees so 100% of my gifts go to the mission",
  "landing.cart.maxLines": "Up to {count} gifts",
  "landing.cart.empty": "Add a gift to get started",
  "landing.cart.submit": "{label} — {amount}",
  "landing.cart.lineSummary": "{fund} — {amount}",
  "landing.cart.lineSummaryMonthly": "{fund} — {amount} monthly",

  // ---- volunteer board (/v/:slug) ----
  "volunteer.loading": "Loading opportunities",
  "volunteer.unavailable": "This volunteer page isn't available.",
  "volunteer.title": "Volunteer with us",
  "volunteer.intro": "Pick a shift that works for you. Times are shown in {zone}.",
  "volunteer.none": "There are no volunteer opportunities open right now — check back soon.",
  "volunteer.manageLink": "Already signed up? View or cancel your shifts",
  "volunteer.requirements": "Requirements: ",
  "volunteer.noShifts": "No upcoming shifts.",
  "volunteer.signUp": "Sign up",
  "volunteer.joinWaitlist": "Join waitlist",
  "volunteer.actionFor": "{action} for {when}",
  "volunteer.open": "Open",
  "volunteer.spotsLeft.one": "{count} spot left",
  "volunteer.spotsLeft.other": "{count} spots left",
  "volunteer.fullWaitlist": "Full — waitlist open",
  "volunteer.full": "Full",
  "volunteer.agreeRequired": "Please agree to the requirements to sign up",
  "volunteer.waitlisted": "You're on the waitlist.",
  "volunteer.waitlistedAt": "You're on the waitlist (#{position}).",
  "volunteer.signedUp": "You're signed up — thank you!",
  "volunteer.waitlistNote": "We'll move you up automatically if a spot opens.",
  "volunteer.cancelNote": "Need to cancel? Use “View or cancel your shifts” below.",
  "volunteer.formLabel": "Volunteer signup",
  "volunteer.agree": "I have read and agree to the requirements above.",
  "volunteer.joinTheWaitlist": "Join the waitlist",
  "volunteer.confirm": "Confirm signup",
  "volunteer.linkSent": "If {email} has volunteer shifts with us, a one-time link to manage them is on its way.",
  "volunteer.devLink": "Open my shifts (dev shortcut)",
  "volunteer.requestLinkLabel": "Request a link to your shifts",
  "volunteer.backToBoard": "← All volunteer opportunities",
  // CT-RM-36: background checks
  "volunteer.backgroundCheck": "Background check required.",
  "volunteer.backgroundCheckNote": "Your spot is held until your background check clears. We'll contact you about next steps.",
  "volunteer.pendingScreening": "You're signed up — pending a background check.",
  "volunteer.pendingScreeningNote": "Your spot is held while we complete a background check. We'll be in touch.",

  // ---- peer-to-peer (/p/:slug/:campaign…) ----
  "p2p.loadingPage": "Loading page",
  "p2p.loadingFundraiser": "Loading fundraising page",
  "p2p.loadingTeam": "Loading team page",
  "p2p.unavailable": "This fundraising page isn't available.",
  "p2p.teamUnavailable": "This team page isn't available.",
  "p2p.raisedOfGoal": "raised of {goal} goal",
  "p2p.raised": "raised",
  "p2p.noOneYet": "No one yet — be the first!",
  "p2p.recentDonors": "Recent donors",
  "p2p.beFirst": "Be the first to give.",
  "p2p.ends": "Ends {date}",
  "p2p.starts": "Starts {date}",
  "p2p.campaignProgress": "Campaign progress",
  "p2p.fundraisers.one": "{count} fundraiser",
  "p2p.fundraisers.other": "{count} fundraisers",
  "p2p.donors.one": "{count} donor",
  "p2p.donors.other": "{count} donors",
  "p2p.members.one": "{count} member",
  "p2p.members.other": "{count} members",
  "p2p.startFundraising": "Start fundraising",
  "p2p.registrationClosed": "Registration closed",
  "p2p.manageMyPage": "Manage my page",
  "p2p.topFundraisers": "Top fundraisers",
  "p2p.topTeams": "Top teams",
  "p2p.goalInvalid": "Enter a goal amount like 500 or 500.00",
  "p2p.chooseTeamError": "Choose a team to join",
  "p2p.registrationComplete": "Registration complete",
  "p2p.pendingApproval": "Thanks! Your page is waiting for approval.",
  "p2p.pageLive": "Your fundraising page is live!",
  "p2p.linkEmailed": "We've emailed you a one-time link to edit your page.",
  "p2p.viewMyPage": "View my page",
  "p2p.devManage": "Manage my page (dev shortcut)",
  "p2p.createTitle": "Create your fundraising page",
  "p2p.emailHint": "Never shown on your page",
  "p2p.yourGoal": "Your goal ({currency})",
  "p2p.pageTitle": "Page title",
  "p2p.pageTitlePlaceholder": "e.g. Jane runs for clean rivers",
  "p2p.story": "Your story",
  "p2p.storyHint": "Optional — why this matters to you",
  "p2p.photo": "Photo link",
  "p2p.photoHint": "Optional — an https:// image URL",
  "p2p.team": "Team",
  "p2p.teamOption": "Team option",
  "p2p.onMyOwn": "Fundraise on my own",
  "p2p.joinTeam": "Join a team",
  "p2p.startTeam": "Start a team (I'll be captain)",
  "p2p.chooseTeam": "Choose a team",
  "p2p.chooseTeamOption": "Choose a team…",
  "p2p.teamName": "Team name",
  "p2p.createMyPage": "Create my page",
  "p2p.linkSent": "If {email} has a fundraising page in this campaign, a one-time link to manage it is on its way.",
  "p2p.requestLinkLabel": "Request a link to manage your page",
  "p2p.give": "Give",
  "p2p.notAccepting": "This page isn't accepting gifts right now.",
  "p2p.givingUnavailable": "Online giving isn't available yet.",
  "p2p.thankYouTitle": "Thank you",
  "p2p.thanksForGift": "Thank you for your gift of {amount}!",
  "p2p.receiptOnWay": "Receipt {number} is on its way to your inbox.",
  "p2p.amountError": "Choose an amount of at least 1.00",
  "p2p.donate": "Donate",
  "p2p.makeGift": "Make a gift",
  "p2p.testMode": "Test mode — no card is charged.",
  "p2p.giftAmount": "Gift amount",
  "p2p.otherAmount": "Other amount",
  "p2p.other": "Other",
  "p2p.receiptEmailHint": "For your receipt — never shown publicly",
  "p2p.hideName": "Hide my name and amount on this page",
  "p2p.giveAmount": "Give {amount}",
  "p2p.frequency": "How often",
  "p2p.oneTime": "One-time",
  "p2p.monthly": "Monthly",
  "p2p.giveMonthlyAmount": "Give {amount} monthly",
  "p2p.thanksForMonthly": "Thank you for your monthly gift of {amount}! Your first payment is done.",
  "p2p.message": "Leave a message",
  "p2p.messageHint": "Optional — shown with your gift on this page",
  "p2p.messagePending": "Your message will appear on the page once it has been reviewed.",
  "p2p.fundraisingFor": "{name} is fundraising for {campaign}",
  "p2p.withTeam": " with team {team}",
  "p2p.fundraiserProgress": "Fundraiser progress",
  "p2p.personProgress": "{name}'s progress",
  "p2p.teamFundraisingFor": "A team fundraising for {campaign}",
  "p2p.captain": " · Captain: {name}",
  "p2p.teamProgress": "Team progress",
  "p2p.teamProgressOf": "{name} progress",
  "p2p.teamMembers": "Team members",
  "p2p.giveTo": "Give to {name} ↗",
  "p2p.joinOrGive": "Join or give ↗",

  // ---- staff app (English-only for now; uses the framework so it can be translated later) ----
  "staff.language.title": "Language",
  "staff.language.appLanguage": "App language",
  "staff.language.appLanguageHint": "Used on screens that have been translated; most of the staff app is English-only for now.",
  "staff.language.orgDefault": "Organization default ({language})",
  "staff.language.preferred": "Preferred language",
  "staff.language.preferredHint": "Receipts, acknowledgments and letters use this language when a translation exists.",
  "staff.language.useDefault": "Organization default",
  "staff.orgLanguages.title": "Languages",
  "staff.orgLanguages.intro":
    "Write content in your default language, then add human-written translations for the other languages you offer. Visitors pick a language on public pages; constituents get receipts and messages in their preferred language when a translation exists.",
  "staff.orgLanguages.default": "Default language",
  "staff.orgLanguages.offered": "Offered languages",
  "staff.orgLanguages.uiNote": "{languages} public pages have built-in translated buttons and labels; other languages show your translated content with English page chrome.",
  "staff.orgLanguages.save": "Save languages",
  "staff.orgLanguages.saved": "Languages saved",
  "staff.translations.title": "Translations",
  "staff.translations.intro": "Human-written versions of this content. Leave a field blank to show the {language} original.",
  "staff.translations.noLanguages": "Your organization only offers {language}. Add languages in Settings → Languages to translate this.",
  "staff.translations.language": "Language",
  "staff.translations.original": "Original ({language})",
  "staff.translations.progress": "{done} of {total} translated",
  "staff.translations.save": "Save translation",
  "staff.translations.saved": "{language} translation saved",
  "staff.translations.remove": "Remove translation",
  "staff.translations.removed": "{language} translation removed",
  "staff.translations.nothing": "Nothing to translate yet — add some content first.",
  "staff.translations.saveFirst": "Save this first, then add translations.",
  // CT-RM-33: Settings › Security (SCIM provisioning)
  "staff.security.title": "Security",
  "staff.security.scimTitle": "SCIM user provisioning",
  "staff.security.scimIntro":
    "Let your identity provider (Okta, Microsoft Entra ID, or any SCIM 2.0 client) create, update and deactivate CauseTide users. Groups map to roles by name. Deactivated users keep their history; their sessions and tokens are revoked.",
  "staff.security.baseUrl": "SCIM base URL",
  "staff.security.copy": "Copy",
  "staff.security.copied": "Copied to clipboard",
  "staff.security.managedUsers.one": "{count} user is managed by SCIM.",
  "staff.security.managedUsers.other": "{count} users are managed by SCIM.",
  "staff.security.tokens": "SCIM tokens",
  "staff.security.tokensHint": "A SCIM token can only call the SCIM API. It is shown once; store it in your identity provider.",
  "staff.security.newToken": "New SCIM token",
  "staff.security.noTokens": "No SCIM tokens yet.",
  "staff.security.colName": "Name",
  "staff.security.colToken": "Token",
  "staff.security.colCreatedBy": "Created by",
  "staff.security.colLastUsed": "Last used",
  "staff.security.never": "never",
  "staff.security.revoke": "Revoke",
  "staff.security.revokeConfirm": "Revoke \"{name}\"? Provisioning with it stops immediately.",
  "staff.security.revoked": "SCIM token \"{name}\" revoked",
  "staff.security.createTitle": "New SCIM token",
  "staff.security.tokenName": "Name",
  "staff.security.tokenNamePlaceholder": "e.g. Okta production",
  "staff.security.create": "Create token",
  "staff.security.cancel": "Cancel",
  "staff.security.nameRequired": "Give the token a name",
  "staff.security.createdTitle": "Your SCIM token",
  "staff.security.createdLabel": "SCIM token",
  "staff.security.createdNote": "Copy this token into your identity provider now. You won't be able to see it again.",
  "staff.security.sendInvites": "Send invitations to SCIM-created users",
  "staff.security.sendInvitesHint": "Off: provisioned users are active right away and get no invitation. On: they go through the usual invite flow.",
  "staff.security.saved": "Saved",
  "staff.users.managedBySCIM": "Managed by SCIM",
  "staff.users.managedBySCIMHint": "Name, email and status come from your identity provider.",
  // CT-RM-36: volunteer background checks
  "staff.screening.title": "Background check",
  "staff.screening.status.not_started": "Not started",
  "staff.screening.status.invited": "Invited",
  "staff.screening.status.pending": "In progress",
  "staff.screening.status.clear": "Clear",
  "staff.screening.status.consider": "Consider",
  "staff.screening.status.expired": "Expired",
  "staff.screening.status.cancelled": "Cancelled",
  "staff.screening.testMode": "Test mode — no real check is run and nothing is sent.",
  "staff.screening.providerTest": "Test-mode provider",
  "staff.screening.reference": "Reference",
  "staff.screening.completed": "Completed {date}",
  "staff.screening.expires": "Expires {date}",
  "staff.screening.expiringSoon": "Expiring soon",
  "staff.screening.none": "No background check on file.",
  "staff.screening.held.one": "{count} signup is held for this check",
  "staff.screening.held.other": "{count} signups are held for this check",
  "staff.screening.start": "Start background check",
  "staff.screening.submit": "Submit to provider",
  "staff.screening.consentTitle": "Start a background check",
  "staff.screening.consentLabel": "The volunteer gave consent in the app",
  "staff.screening.consentHint": "Consent is recorded here; no consent email is sent. Never store SSNs or identity documents in CauseTide.",
  "staff.screening.consentRequired": "Confirm the volunteer's consent first",
  "staff.screening.saveWithoutSubmitting": "Save without submitting",
  "staff.screening.started": "Background check started",
  "staff.screening.submitted": "Background check submitted",
  "staff.screening.simulate": "Simulate result",
  "staff.screening.simulated": "Result recorded: {status}",
  "staff.screening.cancel": "Cancel check",
  "staff.screening.cancelConfirm": "Cancel this background check?",
  "staff.screening.cancelled": "Background check cancelled",
  "staff.screening.history.one": "{count} earlier check",
  "staff.screening.history.other": "{count} earlier checks",
  "staff.screening.close": "Close",
  "staff.screening.requireLabel": "Require a background check",
  "staff.screening.requireHint": "Signups are held until the volunteer's check clears. Staff can override a hold.",
  "staff.screening.validityLabel": "Check valid for (months)",
  "staff.screening.validityHint": "Blank: until the check itself expires",
  "staff.screening.required": "Background check required",
  "staff.screening.requiredFor": "Background check required · valid {months} months",
  "staff.screening.cardBadge": "background check",
  "staff.screening.heldCount": "{count} held for check",
  "staff.screening.heldBadge": "held for background check",
  "staff.screening.checkBadge": "check: {status}",
  "staff.screening.overridden": "hold overridden",
  "staff.screening.override": "Override hold",
  "staff.screening.overrideTitle": "Override background check hold — {name}",
  "staff.screening.overrideReason": "Reason",
  "staff.screening.overrideHint": "Recorded in the audit log and on the volunteer's timeline.",
  "staff.screening.overrideDone": "{name} is signed up",
  "staff.screening.overrideReasonRequired": "Give a reason (at least 3 characters)",
  "staff.screening.tabOpportunities": "Opportunities",
  "staff.screening.tabChecks": "Background checks",
  "staff.screening.noticeHeld.one": "{count} signup is held for a background check",
  "staff.screening.noticeHeld.other": "{count} signups are held for a background check",
  "staff.screening.noticeExpiring.one": "{count} check expires within 30 days",
  "staff.screening.noticeExpiring.other": "{count} checks expire within 30 days",
  "staff.screening.review": "Review",
  "staff.screening.filterLabel": "Filter volunteers by background check",
  "staff.screening.filterAll": "All",
  "staff.screening.filterExpiring": "Expiring soon",
  "staff.screening.filterHeld": "Held signups",
  "staff.screening.colVolunteer": "Volunteer",
  "staff.screening.colStatus": "Status",
  "staff.screening.colCompleted": "Completed",
  "staff.screening.colExpires": "Expires",
  "staff.screening.colHeld": "Held signups",
  "staff.screening.colUpcoming": "Upcoming shifts",
  "staff.screening.empty": "No volunteers match this filter.",
  "staff.screening.runSweep": "Check expiries now",
  "staff.screening.sweepDone": "{expired} expired, {flagged} expiring soon, {tasks} tasks created",

  // CT-RM-35: Settings › Integrations › Data sync
  "staff.sync.title": "Data sync",
  "staff.sync.intro": "Keep constituents and gifts in step with another system. Each connection maps fields one by one and decides who wins when both sides change the same record. Runs are manual: use Sync now.",
  "staff.sync.noPermission": "You need the Manage organization permission to set up data sync.",
  "staff.sync.newConnection": "New connection",
  "staff.sync.empty": "No sync connections yet",
  "staff.sync.emptyHint": "Create a connection to the Test CRM to try two-way sync safely.",
  "staff.sync.testModeBadge": "Test mode — no real system is contacted",
  "staff.sync.status.active": "Active",
  "staff.sync.status.paused": "Paused",
  "staff.sync.pausedHint": "Paused: local changes keep queuing and are sent when you resume.",
  "staff.sync.conflictPolicy": "When both sides changed",
  "staff.sync.policy.newest_wins": "Newest change wins",
  "staff.sync.policy.newest_wins.hint": "The most recent edit is kept, whichever side made it.",
  "staff.sync.policy.causetide_wins": "CauseTide wins",
  "staff.sync.policy.causetide_wins.hint": "CauseTide's value is kept and pushed back to the remote system.",
  "staff.sync.policy.remote_wins": "Remote system wins",
  "staff.sync.policy.remote_wins.hint": "The remote system's value overwrites CauseTide.",
  "staff.sync.pendingOutbound": "Waiting to send",
  "staff.sync.failedRecords": "Failed records",
  "staff.sync.lastRun": "Last run",
  "staff.sync.never": "Never",
  "staff.sync.syncNow": "Sync now",
  "staff.sync.pause": "Pause",
  "staff.sync.resume": "Resume",
  "staff.sync.showLog": "Show run log",
  "staff.sync.edit": "Edit",
  "staff.sync.delete": "Delete",
  "staff.sync.deleteConfirm": "Delete the connection \"{name}\"? Its run log is removed too. Records already synced stay where they are.",
  "staff.sync.deleted": "Connection \"{name}\" deleted",
  "staff.sync.pausedToast": "\"{name}\" paused",
  "staff.sync.resumedToast": "\"{name}\" resumed",
  "staff.sync.pausedError": "This connection is paused. Resume it to sync.",
  "staff.sync.runFinished": "Sync finished: {pushed} sent, {pulled} received, {failed} failed",
  "staff.sync.newTitle": "New sync connection",
  "staff.sync.editTitle": "Edit \"{name}\"",
  "staff.sync.connector": "Connect to",
  "staff.sync.name": "Connection name",
  "staff.sync.constituentMapping": "Constituent fields",
  "staff.sync.syncConstituents": "Sync constituents",
  "staff.sync.directionHint": "In: the remote system updates CauseTide. Out: CauseTide updates the remote system. Both: two-way.",
  "staff.sync.giftMapping": "Gift fields",
  "staff.sync.syncGifts": "Send gifts",
  "staff.sync.giftOutboundNote": "Gifts are sent out only. Gifts are never created or changed in CauseTide from the remote system.",
  "staff.sync.colLocalField": "CauseTide field",
  "staff.sync.colRemoteField": "Remote field",
  "staff.sync.colDirection": "Direction",
  "staff.sync.localFieldRow": "CauseTide field, row {n}",
  "staff.sync.remoteFieldRow": "Remote field, row {n}",
  "staff.sync.directionRow": "Direction, row {n}",
  "staff.sync.removeRow": "Remove row {n}",
  "staff.sync.giftLocalFieldRow": "Gift field, row {n}",
  "staff.sync.giftRemoteFieldRow": "Remote gift field, row {n}",
  "staff.sync.removeGiftRow": "Remove gift row {n}",
  "staff.sync.remove": "Remove",
  "staff.sync.addField": "Add field",
  "staff.sync.duplicateMapping": "Each field can be mapped only once on each side.",
  "staff.sync.direction.in": "In",
  "staff.sync.direction.out": "Out",
  "staff.sync.direction.both": "Both ways",
  "staff.sync.field.firstName": "First name",
  "staff.sync.field.lastName": "Last name",
  "staff.sync.field.middleName": "Middle name",
  "staff.sync.field.prefix": "Prefix",
  "staff.sync.field.suffix": "Suffix",
  "staff.sync.field.nickname": "Nickname",
  "staff.sync.field.orgName": "Organization name",
  "staff.sync.field.birthDate": "Birth date",
  "staff.sync.field.email": "Email",
  "staff.sync.field.phone": "Phone",
  "staff.sync.field.addressLine1": "Address line 1",
  "staff.sync.field.addressLine2": "Address line 2",
  "staff.sync.field.city": "City",
  "staff.sync.field.state": "State / region",
  "staff.sync.field.postalCode": "Postal code",
  "staff.sync.field.country": "Country",
  "staff.sync.giftField.amount": "Amount",
  "staff.sync.giftField.date": "Gift date",
  "staff.sync.giftField.fund": "Fund",
  "staff.sync.giftField.paymentMethod": "Payment method",
  "staff.sync.create": "Create connection",
  "staff.sync.saveChanges": "Save changes",
  "staff.sync.cancel": "Cancel",
  "staff.sync.saved": "Connection \"{name}\" saved",
  "staff.sync.runLogTitle": "Run log — {name}",
  "staff.sync.retryAllFailed": "Retry all failed",
  "staff.sync.retriedAll.one": "{count} record queued for the next run",
  "staff.sync.retriedAll.other": "{count} records queued for the next run",
  "staff.sync.retryQueued": "Queued for the next run",
  "staff.sync.retry": "Retry",
  "staff.sync.noRuns": "No runs yet. Use Sync now to run this connection.",
  "staff.sync.colStarted": "Started",
  "staff.sync.colTrigger": "Trigger",
  "staff.sync.colStatus": "Status",
  "staff.sync.count.pushed": "Sent",
  "staff.sync.count.pulled": "Received",
  "staff.sync.count.created": "Created",
  "staff.sync.count.updated": "Updated",
  "staff.sync.count.conflicts": "Conflicts",
  "staff.sync.count.skipped": "Skipped",
  "staff.sync.count.failed": "Failed",
  "staff.sync.trigger.manual": "Manual",
  "staff.sync.trigger.retry": "Retry",
  "staff.sync.runStatus.running": "Running",
  "staff.sync.runStatus.completed": "Completed",
  "staff.sync.runStatus.failed": "Failed",
  "staff.sync.viewRecords": "View records",
  "staff.sync.recordsTitle": "Records in the run started {started}",
  "staff.sync.filterStatus": "Filter by status",
  "staff.sync.allStatuses": "All statuses",
  "staff.sync.recordStatus.pending": "Pending",
  "staff.sync.recordStatus.synced": "Synced",
  "staff.sync.recordStatus.skipped": "Skipped",
  "staff.sync.recordStatus.failed": "Failed",
  "staff.sync.noRecords": "No records in this run.",
  "staff.sync.colObject": "Record type",
  "staff.sync.colLocalId": "CauseTide ID",
  "staff.sync.colRemoteId": "Remote ID",
  "staff.sync.colOutcome": "Outcome",
  "staff.sync.colError": "Error",
  "staff.sync.recordDirection.in": "In",
  "staff.sync.recordDirection.out": "Out",
  "staff.sync.object.constituent": "Constituent",
  "staff.sync.object.gift": "Gift",
  "staff.sync.outcome.created": "Created",
  "staff.sync.outcome.updated": "Updated",
  "staff.sync.outcome.unchanged": "Unchanged",
  "staff.sync.outcome.echo": "Echo of our own change",
  "staff.sync.outcome.superseded": "Superseded by a newer change",
  "staff.sync.outcome.conflictCausetide": "Conflict: CauseTide kept",
  "staff.sync.outcome.conflictRemote": "Conflict: remote kept",
  "staff.sync.sandboxTitle": "Test CRM sandbox",
  "staff.sync.sandboxIntro": "The Test CRM is a pretend remote system stored with your data. Simulate an edit someone made there, then use Sync now to watch it come in.",
  "staff.sync.sandboxEmpty": "The Test CRM is empty. Sync now sends your constituents there.",
  "staff.sync.colData": "Data",
  "staff.sync.colOrigin": "Last written by",
  "staff.sync.colUpdated": "Updated",
  "staff.sync.origin.causetide": "CauseTide (sync)",
  "staff.sync.origin.remote": "Remote edit",
  "staff.sync.sandboxEditTitle": "Simulate a remote contact edit",
  "staff.sync.sandboxRemoteId": "Remote ID",
  "staff.sync.sandboxRemoteIdHint": "Leave blank to add a new remote contact.",
  "staff.sync.sandboxChangedAt": "Changed at",
  "staff.sync.sandboxChangedAtHint": "Optional: backdate the change to test conflicts.",
  "staff.sync.sandboxSubmit": "Save remote edit",
  "staff.sync.sandboxNeedField": "Fill in at least one field",
  "staff.sync.sandboxSaved": "Remote contact {id} saved in the Test CRM",

  // ---- CT-RM-34: custom objects ----
  "staff.objects.title": "Custom objects",
  "staff.objects.intro": "Track what your organization manages beyond donors and gifts (scholarships, properties, board terms) with your own fields, linked to constituents.",
  "staff.objects.new": "New object",
  "staff.objects.empty": "No custom objects yet",
  "staff.objects.emptyHint": "Define one to track records like scholarships or properties and link them to constituents.",
  "staff.objects.emptyReadOnly": "Ask an admin to define custom objects in Settings.",
  "staff.objects.name": "Name (singular)",
  "staff.objects.plural": "Name (plural)",
  "staff.objects.key": "Key",
  "staff.objects.keyHint": "Used by the API, imports and segments. It can't be changed later.",
  "staff.objects.icon": "Icon",
  "staff.objects.description": "Description",
  "staff.objects.create": "Create object",
  "staff.objects.created": "{name} created",
  "staff.objects.saved": "Changes saved",
  "staff.objects.archive": "Archive",
  "staff.objects.restore": "Restore",
  "staff.objects.archived": "archived",
  "staff.objects.required": "required",
  "staff.objects.delete": "Delete",
  "staff.objects.deleteConfirm": "Delete \"{name}\"? Its field definitions are deleted too.",
  "staff.objects.deleted": "{name} deleted",
  "staff.objects.fields": "Fields",
  "staff.objects.fieldsHint": "These are custom fields: the same types, validation and field-level security as constituent fields.",
  "staff.objects.addField": "Add field",
  "staff.objects.newFieldTitle": "New field on {plural}",
  "staff.objects.fieldCreated": "Field created",
  "staff.objects.noFields": "No fields yet. Every record has a name; add fields for everything else.",
  "staff.objects.records.one": "{count} record",
  "staff.objects.records.other": "{count} records",
  "staff.objects.viewRecords": "View records",
  "staff.objects.cancel": "Cancel",
  "staff.objects.colLabel": "Label",
  "staff.objects.colKey": "Key",
  "staff.objects.colType": "Type",
  "staff.objects.colName": "Name",
  "staff.objects.colLinked": "Links",
  "staff.objects.search": "Search {plural}",
  "staff.objects.newRecord": "New {name}",
  "staff.objects.exportCsv": "Export CSV",
  "staff.objects.noRecords": "No {plural} yet",
  "staff.objects.noMatches": "Nothing matches this search or filter.",
  "staff.objects.sortBy": "Sort by",
  "staff.objects.sortCreated": "Date created",
  "staff.objects.sortUpdated": "Last updated",
  "staff.objects.ascending": "Ascending",
  "staff.objects.descending": "Descending",
  "staff.objects.filterField": "Filter by",
  "staff.objects.filterValue": "Filter value",
  "staff.objects.noFilter": "No filter",
  "staff.objects.loadMore": "Load more",
  "staff.objects.recordName": "Name",
  "staff.objects.back": "All {plural}",
  "staff.objects.details": "Details",
  "staff.objects.save": "Save changes",
  "staff.objects.deleteRecordConfirm": "Delete \"{name}\"? Its links to constituents and gifts are removed.",
  "staff.objects.links": "Linked constituents and gifts",
  "staff.objects.noLinks": "Not linked to anyone yet.",
  "staff.objects.linkConstituent": "Link constituent",
  "staff.objects.link": "Link",
  "staff.objects.role": "Role",
  "staff.objects.rolePlaceholder": "e.g. Recipient",
  "staff.objects.unlink": "Unlink",
  "staff.objects.linked": "{name} linked",
  "staff.objects.unlinked": "Link removed",
  "staff.objects.notFound": "Record not found",
  "staff.objects.cardTitle": "Related records",
  "staff.objects.add": "Add",
  "staff.objects.addRecord": "Add {name}",
  "staff.objects.addLinkedTitle": "New {name} for {constituent}",
  "staff.objects.yes": "Yes",
  "staff.objects.no": "No",
} as const;

/** a catalog key, or a plural stem ("p2p.donors" for p2p.donors.one/.other) resolved by {count} */
export type MessageKey = keyof typeof en | (keyof typeof en extends infer K ? (K extends `${infer Stem}.one` ? Stem : never) : never);
export type Catalog = Partial<Record<MessageKey, string>>;
